import type { ProblemDetails } from '@swarm/api-client';
import { IndexedDbQueueStore, type QueueKind, type QueueStore, type QueuedRequest } from './queue';

/** Writes that may be captured offline (ADR 0027), by path of the API. */
const QUEUEABLE: { kind: QueueKind; pattern: RegExp }[] = [
  { kind: 'treatment', pattern: /^\/api\/v1\/treatments$/ },
  { kind: 'feeding', pattern: /^\/api\/v1\/feedings$/ },
  { kind: 'miteCount', pattern: /^\/api\/v1\/colonies\/[^/]+\/mite-counts$/ },
  { kind: 'note', pattern: /^\/api\/v1\/colonies\/[^/]+\/notes$/ },
  { kind: 'taskComplete', pattern: /^\/api\/v1\/tasks\/[^/]+\/complete$/ },
  { kind: 'marketSale', pattern: /^\/api\/v1\/market-days\/[^/]+\/sales$/ },
];

export const QUEUED_HEADER = 'x-swarm-queued';
const CHANGED = 'swarm:queue-changed';
const ONLINE_KEY = 'swarm.lastOnlineAt';
const OWNER_KEY = 'swarm.offlineOwner';
/** Web Lock shared by all tabs, so only one of them sends the queue at a time. */
export const FLUSH_LOCK = 'swarm-flush';

/** The part of the Web Locks API the queue needs (`navigator.locks`). */
export interface FlushLocks {
  request<T>(
    name: string,
    options: { ifAvailable: true },
    callback: (lock: unknown) => Promise<T>,
  ): Promise<T>;
}

export function queueKindOf(method: string, url: string): QueueKind | null {
  if (method !== 'POST') return null;
  const path = new URL(url, 'http://localhost').pathname;
  return QUEUEABLE.find((q) => q.pattern.test(path))?.kind ?? null;
}

/** What the caller of a queued write gets back instead of the created record. */
export function queuedBody(kind: QueueKind, body: string | null): unknown {
  const payload = body ? (JSON.parse(body) as { colony_ids?: unknown[] }) : {};
  // Treatments and feedings answer with one record per colony.
  if (kind === 'treatment' || kind === 'feeding') {
    return (payload.colony_ids ?? [null]).map(() => ({ queued: true }));
  }
  return { queued: true };
}

/** True for the placeholder a queued write returns. */
export function isQueued(value: unknown): boolean {
  return (
    typeof value === 'object' && value !== null && (value as { queued?: unknown }).queued === true
  );
}

export function rememberOnline(now = new Date()): void {
  try {
    localStorage.setItem(ONLINE_KEY, now.toISOString());
  } catch {
    // Private mode: the banner then shows no time.
  }
}

export function lastOnline(): Date | null {
  try {
    const value = localStorage.getItem(ONLINE_KEY);
    return value ? new Date(value) : null;
  } catch {
    return null;
  }
}

export interface OfflineOptions {
  store?: QueueStore;
  fetch?: typeof globalThis.fetch;
  now?: () => Date;
  newId?: () => string;
  /** The signed-in user from the start (tests); otherwise see `setOwner`. */
  owner?: string;
  /** Web Locks; defaults to `navigator.locks`, `null` for none. */
  locks?: FlushLocks | null;
}

export interface FlushResult {
  sent: number;
  failed: number;
  /** The network or the server was not reachable; the rest waits. */
  stopped: boolean;
}

/**
 * The offline layer: captures writes while there is no connection and sends
 * them later with their original `Idempotency-Key`, so a retry never books
 * twice (API-006).
 */
export class Offline {
  readonly store: QueueStore;
  private readonly net: typeof globalThis.fetch;
  private readonly now: () => Date;
  private readonly newId: () => string;
  private readonly locks: FlushLocks | null;
  /** Tells the other tabs that the queue changed (e.g. they did not send it themselves). */
  private readonly channel: BroadcastChannel | null =
    typeof BroadcastChannel !== 'undefined' && typeof window !== 'undefined'
      ? new BroadcastChannel(CHANGED)
      : null;
  private flushing: Promise<FlushResult> | null = null;
  /** The signed-in user; entries of others are neither shown nor sent. */
  private owner: string | null = null;

  constructor({ store, fetch, now, newId, owner, locks }: OfflineOptions = {}) {
    this.locks =
      locks !== undefined
        ? locks
        : typeof navigator !== 'undefined' && 'locks' in navigator
          ? (navigator.locks as FlushLocks)
          : null;
    this.owner = owner ?? null;
    this.store = store ?? new IndexedDbQueueStore();
    this.net = fetch ?? ((input, init) => globalThis.fetch(input, init));
    this.now = now ?? (() => new Date());
    this.newId = newId ?? (() => crypto.randomUUID());
    if (this.channel) {
      this.channel.onmessage = () => window.dispatchEvent(new Event(CHANGED));
      // Node (tests) would otherwise keep running for the open channel.
      (this.channel as { unref?: () => void }).unref?.();
    }
  }

  private changed() {
    if (typeof window !== 'undefined') window.dispatchEvent(new Event(CHANGED));
    this.channel?.postMessage('changed');
  }

  /**
   * Sets the signed-in user. When somebody else signs in on this device, the
   * cached answers of the previous user are deleted first.
   */
  async setOwner(id: string): Promise<void> {
    if (this.owner === id) return;
    let previous: string | null = null;
    try {
      previous = localStorage.getItem(OWNER_KEY);
      localStorage.setItem(OWNER_KEY, id);
    } catch {
      // Private mode: nothing is kept across sessions anyway.
    }
    if (previous !== null && previous !== id) await clearApiCache();
    this.owner = id;
    this.changed();
  }

  private mine(item: QueuedRequest): boolean {
    return this.owner !== null && (item.owner == null || item.owner === this.owner);
  }

  /** `fetch` for the API client: sends, or captures a queueable write when offline. */
  fetch = async (input: Request): Promise<Response> => {
    const kind = queueKindOf(input.method, input.url);
    if (!kind) return this.track(await this.net(input));
    // Without a usable store (no IndexedDB, e.g. private mode) writes just go out.
    // Rejected entries wait for a decision and must not hold back new writes.
    const pending = await this.store.all().then(
      (items) => items.some((i) => !i.failure && this.mine(i)),
      () => null,
    );
    if (pending === null) return this.track(await this.net(input));
    const offline = typeof navigator !== 'undefined' && navigator.onLine === false;
    if (!pending && !offline) {
      const copy = input.clone();
      try {
        return this.track(await this.net(input));
      } catch (error) {
        if (!(error instanceof TypeError)) throw error;
        return this.capture(kind, copy).catch(() => {
          throw error;
        });
      }
    }
    return this.capture(kind, input);
  };

  /** Remembers the last time the server answered, for "Stand von …". */
  private track(response: Response): Response {
    // Answers the service worker took from its cache say nothing about the connection.
    if (response.status < 500 && !response.headers.has('x-swarm-cached-at'))
      rememberOnline(this.now());
    return response;
  }

  private async capture(kind: QueueKind, request: Request): Promise<Response> {
    const headers: Record<string, string> = {};
    request.headers.forEach((value, key) => {
      headers[key] = value;
    });
    const body = await request.text();
    await this.store.put({
      id: this.newId(),
      createdAt: this.now().toISOString(),
      method: request.method,
      url: request.url,
      headers,
      body: body || null,
      kind,
      attempts: 0,
      failure: null,
      owner: this.owner,
    });
    this.changed();
    void this.requestBackgroundSync();
    return new Response(JSON.stringify(queuedBody(kind, body || null)), {
      status: 202,
      headers: { 'content-type': 'application/json', [QUEUED_HEADER]: '1' },
    });
  }

  private async requestBackgroundSync() {
    try {
      const registration = await navigator.serviceWorker?.getRegistration();
      await (
        registration as
          | (ServiceWorkerRegistration & { sync?: { register(tag: string): Promise<void> } })
          | undefined
      )?.sync?.register('swarm-flush');
    } catch {
      // Not supported (Safari): the entries go out when the app is open again.
    }
  }

  async pending(): Promise<QueuedRequest[]> {
    return (await this.store.all()).filter((i) => this.mine(i));
  }

  /**
   * Sends the queue in order; stops at the first network or server error.
   * Another tab already sending holds the lock: this one then leaves the
   * queue to it (`stopped`), so no entry goes out twice at the same time.
   */
  flush(): Promise<FlushResult> {
    this.flushing ??= this.locked().finally(() => {
      this.flushing = null;
    });
    return this.flushing;
  }

  private locked(): Promise<FlushResult> {
    if (!this.locks) return this.run();
    return this.locks.request(FLUSH_LOCK, { ifAvailable: true }, (lock) =>
      lock ? this.run() : Promise.resolve({ sent: 0, failed: 0, stopped: true }),
    );
  }

  private async run(): Promise<FlushResult> {
    const result: FlushResult = { sent: 0, failed: 0, stopped: false };
    // Nobody signed in yet: entries must not go out with an unknown session.
    if (this.owner === null) return { ...result, stopped: true };
    for (const item of await this.store.all()) {
      if (item.failure) continue; // waits for a decision of the person
      if (!this.mine(item)) continue; // somebody else's, waits for their session
      let response: Response;
      try {
        response = await this.net(
          new Request(item.url, {
            method: item.method,
            headers: item.headers,
            body: item.body,
            credentials: 'include',
          }),
        );
      } catch {
        result.stopped = true;
        break;
      }
      if (response.ok) {
        await this.store.remove(item.id);
        result.sent += 1;
        rememberOnline(this.now());
      } else if (
        response.status === 401 ||
        response.status >= 500 ||
        response.status === 429 ||
        (await inProgress(response))
      ) {
        // Try again later; an earlier attempt may still be running on the server.
        await this.store.put({ ...item, attempts: item.attempts + 1 });
        result.stopped = true;
        break;
      } else {
        await this.store.put({
          ...item,
          attempts: item.attempts + 1,
          failure: await failureOf(response),
        });
        result.failed += 1;
      }
    }
    this.changed();
    return result;
  }

  async discard(id: string): Promise<void> {
    await this.store.remove(id);
    this.changed();
  }

  /** Deletes the queue and every cached answer of the server (sign-out). */
  async clearPrivateData(): Promise<void> {
    // No usable store (no IndexedDB, e.g. private mode): nothing queued, and
    // sign-out must still reach the server.
    await this.store.clear().catch(() => undefined);
    await clearApiCache();
    try {
      localStorage.removeItem(ONLINE_KEY);
      localStorage.removeItem(OWNER_KEY);
    } catch {
      // ignore
    }
    this.owner = null;
    this.changed();
  }
}

/** Deletes every cached answer of the server and shared files not yet saved. */
async function clearApiCache(): Promise<void> {
  try {
    for (const key of await caches.keys()) {
      if (key.startsWith('swarm-api') || key.startsWith('swarm-share')) await caches.delete(key);
    }
  } catch {
    // No Cache API (tests, old browsers).
  }
}

/** 409 `idempotency_key.in_progress`: the same entry is still being processed. */
async function inProgress(response: Response): Promise<boolean> {
  if (response.status !== 409) return false;
  try {
    const problem = (await response.clone().json()) as ProblemDetails;
    return problem.code === 'idempotency_key.in_progress';
  } catch {
    return false;
  }
}

async function failureOf(response: Response): Promise<string> {
  try {
    const problem = (await response.json()) as ProblemDetails;
    return problem.detail ?? problem.code ?? `HTTP ${response.status}`;
  } catch {
    return `HTTP ${response.status}`;
  }
}

export const queueChangedEvent = CHANGED;
